<template>
    <div class="card w-10" style="max-width: 800px; margin: auto">
        <div class="formgrid grid p-fluid mt-4 justify-content-center">
            <div class="field col-10 md:col-12 lg:col-12 xl:col-12">
                <h1>{{ participanteEncontrado?.nome ? 'Editar Participante' : 'Cadastrar Participante' }}</h1>
                <h4>Informe o CPF do participante</h4>
                <InputText
                    v-if="modoTouchscreen"
                    :modelValue="ocultarCPF ? cpfOculto : cpfMascarado"
                    readonly
                    inputmode="none"
                    :class="'text-4xl'"
                    ref="cpf"
                    @focus="ativar('cpf')"
                    @click="ativar('cpf')"
                    :disabled="cpfSomenteLeitura"
                />
                <InputMask
                    v-else
                    v-model="cpf"
                    mask="999.999.999-99"
                    :unmask="true"
                    :type="ocultarCPF ? 'password' : 'text'"
                    :readonly="cpfSomenteLeitura"
                    @keypress="v$.cpf.$touch()"
                    class="text-4xl"
                    ref="cpf"
                />
                <div>
                    <Checkbox v-model="ocultarCPF" :binary="true" style="margin-top: 1em" />
                    <label for=""> Ocultar CPF</label>
                </div>
                <small class="p-error" v-if="(v$.cpf.required.$invalid || v$.cpf.minLength.$invalid) && v$.cpf.$error">O CPF é obrigatório</small>
                <small class="p-error" v-if="v$.cpf.cpfValido.$invalid && !(v$.cpf.required.$invalid || v$.cpf.minLength.$invalid)">CPF inválido</small>
            </div>
        </div>
        <div class="formgrid grid p-fluid mt-4 justify-content-center">
            <div class="field col-10 md:col-12 lg:col-12 xl:col-12">
                <h4>Informe o nome do participante</h4>
                <InputText v-if="modoTouchscreen" :modelValue="nome" readonly inputmode="none" class="text-4xl" ref="nome" @focus="ativar('nome')" @click="ativar('nome')" />
                <InputText v-else v-model="nome" @input="v$.nome.$touch()" class="text-4xl" ref="nome" />
                <small class="p-error" v-if="v$.nome.$error">O nome é obrigatório</small>
            </div>
        </div>
        <div class="formgrid grid p-fluid mt-4 justify-content-center">
            <div class="field col-10 md:col-12 lg:col-12 xl:col-12">
                <h4>Informe o e-mail do participante</h4>
                <InputText v-if="modoTouchscreen" :modelValue="email" readonly inputmode="none" class="text-4xl" @focus="ativar('email')" @click="ativar('email')" />
                <InputText v-else v-model="email" @input="v$.email.$touch()" class="text-4xl" />
                <small class="p-error" v-if="v$.email.$error">O e-mail é obrigatório</small>
            </div>
        </div>
        <div class="formgrid grid p-fluid mt-4 justify-content-center">
            <div class="field col-10 md:col-12 lg:col-12 xl:col-12">
                <h4>Informe o telefone do participante</h4>
                <InputText v-if="modoTouchscreen" :modelValue="telefoneMascarado" readonly inputmode="none" class="text-4xl" @focus="ativar('telefone')" @click="ativar('telefone')" />
                <InputMask v-else v-model="telefone" mask="(99)99999-9999" :unmask="true" @keypress="v$.telefone.$touch()" class="text-4xl" />
                <small class="p-error" v-if="v$.telefone.$error">O telefone é obrigatório</small>
            </div>
        </div>
        <div class="formgrid grid p-fluid mt-4 justify-content-center">
            <div class="field field-checkbox col-12 md:col-12">
                <Checkbox v-model="consentimento" :binary="true" id="consentimento" />
                <label for="consentimento" class="p-error text-2xl"
                    >ATENÇÃO: Você autoriza este aplicativo capturar informações para que você compartilhe seus dados com o SEBRAE MA</label
                >
            </div>
        </div>
        <div class="flex justify-content-center align-items-center">
            <Button
                icon="pi pi-qrcode"
                @click="gerarQrCode()"
                :label="participanteEncontrado?.nome ? 'Imprimir QR Code' : 'Gerar QRCode'"
                :title="participanteEncontrado?.nome ? 'Clique para imprimir o QR Code do participante' : 'Clique para gerar o QRCode do participante'"
                :disabled="v$.$invalid || !consentimento"
                class="mt-4 text-3xl mr-4"
            />
            <Button icon="pi pi-refresh" @click="$emit('reiniciar')" label="Reiniciar" title="Clique para reiniciar a pesquisa" class="mt-4 text-3xl" />
        </div>
        <TecladoVirtual
            v-if="campoAtivo && modoTouchscreen"
            :layout="layoutTeclado"
            @tecla="onTecla"
            @backspace="onBackspace"
            @limpar="onLimpar"
            @fechar="campoAtivo = null"
        />
    </div>
</template>

<script>
import useVuelidate from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
import TecladoVirtual from '@/shared/components/TecladoVirtual.vue';
import { aplicarCaractere, aplicarBackspace } from '@/shared/libs/tecladoVirtual.js';

export default {
    components: { TecladoVirtual },

    props: {
        cpfInformado: {
            type: String,
            required: true,
        },
        participanteEncontrado: {
            type: Object,
            default: null,
        },
        cpfSomenteLeitura: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['gerar-qrcode', 'reiniciar'],

    setup() {
        return { v$: useVuelidate() };
    },

    data() {
        return {
            cpf: null,
            nome: null,
            email: null,
            telefone: null,
            consentimento: false,
            ocultarCPF: true,
            campoAtivo: null,
        };
    },

    validations() {
        return {
            cpf: {
                required,
                minLength: minLength(11),
                cpfValido: () => {
                    return this.$validarCPF(this.cpf);
                },
            },
            nome: {
                required,
            },
            email: {
                required,
                email,
            },
            telefone: {
                required,
            },
        };
    },

    computed: {
        modoTouchscreen() {
            return this.$route.query.modo === 'touchscreen';
        },
        layoutTeclado() {
            return this.campoAtivo === 'telefone' || this.campoAtivo === 'cpf' ? 'numerico' : 'texto';
        },
        cpfMascarado() {
            return this.$toCpf(this.cpf || '');
        },
        cpfOculto() {
            return (this.cpf || '').replace(/./g, '•');
        },
        telefoneMascarado() {
            return this.$toTel(this.telefone || '');
        },
    },

    watch: {
        participanteEncontrado: {
            handler(newParticipante) {
                if (newParticipante && newParticipante.nome) {
                    this.nome = newParticipante.nome;
                    this.email = newParticipante.email;
                    this.telefone = newParticipante.telefone;
                    this.$nextTick(() => {
                        if (this.v$) {
                            this.v$.nome.$touch();
                            this.v$.email.$touch();
                            this.v$.telefone.$touch();
                        }
                    });
                }
            },
            immediate: true,
        },
    },

    methods: {
        gerarQrCode() {
            let participante = {
                cpf: this.cpf,
                nome: this.nome,
                email: this.email,
                telefone: this.telefone,
                corCordao: this.participanteEncontrado?.corCordao,
                nomeCorCordao: this.participanteEncontrado?.nomeCorCordao,
                classificacao: this.participanteEncontrado?.classificacao,
            };
            this.$emit('gerar-qrcode', participante);
        },
        ativar(campo) {
            if (campo === 'cpf' && this.cpfSomenteLeitura) return;
            this.campoAtivo = campo;
        },
        onTecla(t) {
            if (!this.campoAtivo) return;
            const max = this.campoAtivo === 'cpf' || this.campoAtivo === 'telefone' ? 11 : 100;
            this[this.campoAtivo] = aplicarCaractere(this[this.campoAtivo] || '', t, max);
            this.tocarValidacao();
        },
        onBackspace() {
            if (!this.campoAtivo) return;
            this[this.campoAtivo] = aplicarBackspace(this[this.campoAtivo] || '');
            this.tocarValidacao();
        },
        onLimpar() {
            if (this.campoAtivo) this[this.campoAtivo] = '';
            this.tocarValidacao();
        },
        tocarValidacao() {
            this.$nextTick(() => {
                if (this.v$ && this.v$[this.campoAtivo]) this.v$[this.campoAtivo].$touch();
            });
        },
    },

    mounted() {
        this.cpf = this.cpfInformado;
    },
};
</script>

<style scoped></style>
